/*
 * Infinite scroll for collection pages (Dawn / Feather based themes)
 *
 * How it works:
 *  - Keeps the theme's real pagination in the HTML (good for SEO + no-JS fallback)
 *    and only hides it visually once this script successfully starts.
 *  - Watches a sentinel element near the bottom of the grid.
 *  - Fetches the next page in the background, pulls out the product <li> items,
 *    and appends them to the existing grid.
 *  - Stops permanently once there is no "next" link left.
 */
(function () {
  'use strict';

  var GRID = '#product-grid';
  var GRID_CONTAINER = '#ProductGridContainer';
  var PAGINATION = '.pagination-wrapper';
  var LOAD_AHEAD_PX = 600; // start loading this far before the user reaches the end

  var isLoading = false;
  var isFinished = false;
  var observer = null;
  var sentinel = null;

  /**
   * Find the "next page" URL.
   *
   * NOTE: Dawn's pagination snippet has swapped class names — the previous link
   * uses `pagination__item--next` and the next link uses `pagination__item--prev`.
   * So we deliberately do NOT match on those classes. Instead we rely on document
   * order: the snippet always renders the next-page arrow as the LAST list item.
   */
  function getNextUrl(paginationWrapper) {
    if (!paginationWrapper) return null;
    var list = paginationWrapper.querySelector('.pagination__list');
    if (!list) return null;
    var last = list.querySelector('li:last-child > a.pagination__item-arrow');
    return last && last.getAttribute('href') ? last.getAttribute('href') : null;
  }

  function buildSentinel() {
    var el = document.createElement('div');
    el.className = 'collection-grid__anchor';
    el.setAttribute('aria-hidden', 'true');
    // Forced with !important: theme CSS or a browser ad-blocker extension can
    // otherwise set display:none here, which makes the element zero-sized and
    // silently prevents IntersectionObserver from ever firing.
    el.style.setProperty('display', 'block', 'important');
    el.style.setProperty('width', '100%', 'important');
    el.style.setProperty('height', '1px', 'important');
    el.style.setProperty('visibility', 'visible', 'important');
    return el;
  }

  function buildSpinner() {
    var el = document.createElement('div');
    el.className = 'collection-grid__status';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.style.setProperty('display', 'none', 'important');
    el.style.textAlign = 'center';
    el.style.padding = '2.4rem 0';
    el.style.font = 'inherit';
    el.style.opacity = '0.7';
    el.textContent = 'Loading more products\u2026';
    return el;
  }

  function setLoadingVisible(visible) {
    var status = document.querySelector('.collection-grid__status');
    if (status) status.style.setProperty('display', visible ? 'block' : 'none', 'important');
  }

  function stop() {
    isFinished = true;
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (sentinel && sentinel.parentNode) {
      sentinel.parentNode.removeChild(sentinel);
      sentinel = null;
    }
    setLoadingVisible(false);
  }

  function loadNextPage() {
    if (isLoading || isFinished) return;

    var grid = document.querySelector(GRID);
    var paginationWrapper = document.querySelector(PAGINATION);
    var nextUrl = getNextUrl(paginationWrapper);

    if (!grid || !nextUrl) {
      stop();
      return;
    }

    isLoading = true;
    setLoadingVisible(true);

    fetch(nextUrl)
      .then(function (response) {
        if (!response.ok) throw new Error('Request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector(GRID);
        var newPagination = doc.querySelector(PAGINATION);

        if (newGrid) {
          var items = newGrid.querySelectorAll('li.grid__item');
          for (var i = 0; i < items.length; i++) {
            var item = items[i];
            // Dawn's reveal animations only initialise on page load, so items
            // appended later would stay at opacity:0. Strip them so the new
            // products are actually visible.
            item.classList.remove('scroll-trigger', 'animate--slide-in');
            item.removeAttribute('data-cascade');
            item.style.removeProperty('--animation-order');
            grid.appendChild(document.importNode(item, true));
          }
        }

        // Swap in the newer pagination markup so the next fetch reads the
        // correct "next" link. If the new page has none, we're done.
        if (paginationWrapper) {
          paginationWrapper.innerHTML = newPagination ? newPagination.innerHTML : '';
        }

        // Keep the address bar in sync so a refresh doesn't jump back to page 1.
        try {
          window.history.replaceState({}, '', nextUrl);
        } catch (e) {
          /* non-fatal */
        }

        isLoading = false;
        setLoadingVisible(false);

        if (!getNextUrl(paginationWrapper)) {
          stop();
          return;
        }

        // Move the sentinel back below the newly added products.
        if (sentinel && paginationWrapper && paginationWrapper.parentNode) {
          paginationWrapper.parentNode.insertBefore(sentinel, paginationWrapper);
        }
      })
      .catch(function () {
        // On failure, un-hide the real pagination so the shopper is never stuck.
        isLoading = false;
        setLoadingVisible(false);
        var paginationWrapper = document.querySelector(PAGINATION);
        if (paginationWrapper) paginationWrapper.style.display = '';
        stop();
      });
  }

  function init() {
    // Reset state (this also runs again after filters/sorting rebuild the grid).
    isLoading = false;
    isFinished = false;
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    var grid = document.querySelector(GRID);
    var paginationWrapper = document.querySelector(PAGINATION);
    if (!grid || !paginationWrapper) return;

    // Nothing to load — leave pagination alone and do nothing.
    if (!getNextUrl(paginationWrapper)) return;

    // Required APIs missing (very old browser) — leave pagination working.
    if (!('IntersectionObserver' in window) || !('fetch' in window)) return;

    // Hide pagination only now that we know JS is working.
    paginationWrapper.style.display = 'none';

    if (!document.querySelector('.collection-grid__status')) {
      paginationWrapper.parentNode.insertBefore(buildSpinner(), paginationWrapper);
    }

    sentinel = buildSentinel();
    paginationWrapper.parentNode.insertBefore(sentinel, paginationWrapper);

    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) loadNextPage();
        }
      },
      { rootMargin: LOAD_AHEAD_PX + 'px 0px' }
    );

    observer.observe(sentinel);

    // Safety net: if the sentinel is still not rendered (zero-sized) for any
    // reason, IntersectionObserver will never fire. Fall back to a throttled
    // scroll listener so the feature degrades instead of silently dying.
    setTimeout(function () {
      if (!sentinel) return;
      var rect = sentinel.getBoundingClientRect();
      if (rect.height > 0 || rect.width > 0) return;
      if (window.console) {
        console.warn('[infinite-scroll] Sentinel not rendered; using scroll fallback.');
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      attachScrollFallback();
    }, 300);
  }

  var scrollHandlerAttached = false;

  function attachScrollFallback() {
    if (scrollHandlerAttached) return;
    scrollHandlerAttached = true;

    var ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (ticking || isFinished) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          var grid = document.querySelector(GRID);
          if (!grid) return;
          var bottom = grid.getBoundingClientRect().bottom;
          if (bottom - window.innerHeight < LOAD_AHEAD_PX) loadNextPage();
        });
      },
      { passive: true }
    );
  }

  /**
   * Dawn's facets.js replaces the whole grid container when a filter or sort
   * changes, which destroys our sentinel. Watch for that and re-initialise.
   */
  function watchForFilterUpdates() {
    var container = document.querySelector(GRID_CONTAINER);
    if (!container || !('MutationObserver' in window)) return;

    var debounce;
    new MutationObserver(function () {
      // Ignore our own appends: only re-init if the sentinel is gone.
      if (sentinel && document.body.contains(sentinel)) return;
      clearTimeout(debounce);
      debounce = setTimeout(init, 150);
    }).observe(container, { childList: true, subtree: true });
  }

  function boot() {
    init();
    watchForFilterUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();